/* @jsxImportSource @emotion/react */
import React from 'react'

import { INLINE_BLOCK_BACKGROUND } from './utils'

export type InlineQuoteAuthor = {
  name: string
  role?: string | null
  pictureUrl?: string | null
}

export type InlineQuoteProps = {
  children: React.ReactNode
  /** `big` needs `author.pictureUrl`; without one it falls back to small. */
  size?: 'small' | 'big'
  author?: InlineQuoteAuthor | null
}

const SMALL_BREAKPOINT = '@media screen and (max-width: 40rem)'
const AUTHOR_AVATAR_SIZE = 28
const QUOTE_MARK = '“'
const ROLE_SEPARATOR = '·'

const CARD_CLASS = 'margin-vertical-24 radius-semilarger overflow-hidden'
const cardCss = { background: INLINE_BLOCK_BACKGROUND } as const

// &&& outranks .simple-rich-text blockquote and its grey left bar.
const quoteTextCss = {
  '&&&': {
    margin: 0,
    padding: 0,
    fontSize: '1.4rem',
    lineHeight: 1.4,
    fontWeight: 600,
    color: 'var(--shade-00)',
    textWrap: 'pretty',
    whiteSpace: 'pre-line',
  },
  '&&&::before': { content: 'none' },
} as const

const smallQuoteTextCss = {
  '&&&': {
    fontSize: '1.1rem',
    lineHeight: 1.6,
  },
} as const

const quoteMarkCss = {
  fontSize: 80,
  lineHeight: 1,
  height: 32,
  color: 'rgba(0,0,0,0.2)',
} as const

const smallLayoutCss = {
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  columnGap: 24,
  padding: '24px 32px',
  [SMALL_BREAKPOINT]: { padding: '28px 24px', columnGap: 16 },
} as const

const bigLayoutCss = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 240px) minmax(0, 1fr)',
  [SMALL_BREAKPOINT]: { gridTemplateColumns: 'minmax(0, 1fr)' },
} as const

const bigPhotoCss = {
  '&&&': {
    display: 'block',
    width: '100%',
    height: '100%',
    minHeight: 260,
    objectFit: 'cover',
    borderRadius: 0,
    [SMALL_BREAKPOINT]: { height: 240, minHeight: 0 },
  },
} as const

const bigContentCss = {
  padding: '24px 32px',
  [SMALL_BREAKPOINT]: { padding: '24px 24px 28px' },
} as const

const avatarCss = {
  '&&&': {
    width: AUTHOR_AVATAR_SIZE,
    height: AUTHOR_AVATAR_SIZE,
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  },
} as const

function QuoteMark() {
  return (
    <div
      className="font-family-title user-select-none"
      css={quoteMarkCss}
      aria-hidden="true"
    >
      {QUOTE_MARK}
    </div>
  )
}

function AuthorLine(props: { author: InlineQuoteAuthor; withAvatar: boolean }) {
  const { author } = props
  return (
    <figcaption
      className="flexbox flexbox-align-center flexbox-gap-12"
      css={{ fontSize: 'var(--font-size-7, 0.75rem)' }}
    >
      {props.withAvatar && author.pictureUrl && (
        <img src={author.pictureUrl} alt="" loading="lazy" css={avatarCss} />
      )}
      <span>
        <span className="shade-00 font-weight-semibold">{author.name}</span>
        {author.role && (
          <span className="shade-20">
            <span className="padding-horizontal-8" aria-hidden="true">
              {ROLE_SEPARATOR}
            </span>
            {author.role}
          </span>
        )}
      </span>
    </figcaption>
  )
}

export function InlineQuote({
  children,
  size = 'small',
  author,
}: InlineQuoteProps) {
  if (size === 'big' && author?.pictureUrl) {
    return (
      <figure className={CARD_CLASS} css={[cardCss, bigLayoutCss]}>
        <img src={author.pictureUrl} alt="" loading="lazy" css={bigPhotoCss} />
        <div
          className="flexbox flexbox-column flexbox-gap-16"
          css={bigContentCss}
        >
          <QuoteMark />
          <blockquote css={quoteTextCss}>{children}</blockquote>
          <div className="margin-top-auto padding-top-24">
            <AuthorLine author={author} withAvatar={false} />
          </div>
        </div>
      </figure>
    )
  }

  return (
    <figure className={CARD_CLASS} css={[cardCss, smallLayoutCss]}>
      <QuoteMark />
      <div className="flexbox flexbox-column flexbox-gap-20">
        <blockquote css={[quoteTextCss, smallQuoteTextCss]}>
          {children}
        </blockquote>
        {author && <AuthorLine author={author} withAvatar />}
      </div>
    </figure>
  )
}
